<template>
  <div class="claim-media-gallery-viewer">
    <div class="claim-media-thumbs">
      <div v-for="m of media" :key="m.id">
        <img
          :src="m.thumbnail"
          :alt="m.id"
          :class="{ selected: currentMedia && currentMedia.id == m.id, private: m.isPrivate }"
          @click="selectMedia(m)"
        />
      </div>
    </div>

    <div class="claim-selected-media">
      <div class="selected-media-main">
        <div v-if="!currentMedia"><p>Nothing Selected</p></div>
        <video v-else-if="currentMedia.isVideo" :src="currentMedia.link" controls></video>
        <zoomable-image v-else :src="currentMedia.link" :alt="currentMedia.id" />
      </div>
      <div class="checkbox-share" v-if="isSharable">
        <input type="checkbox" :checked="!currentMedia.isPrivate" @change="onToggleCheckbox" />
        Share with vendor
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref } from 'vue';
import { IClaimMedia } from '@/types';
import ZoomableImage from '@/components/ZoomableImage.vue';

export default defineComponent({
  name: 'ClaimMediaGallery',
  components: {
    ZoomableImage,
  },
  props: {
    media: Array,
    isSharable: {
      // flag to display the share with vendor checkbox
      type: Boolean,
      default: true,
    },
    selectedMedia: Object,
  },
  emits: ['toggleShare'],
  setup(props, { emit }) {
    const currentMedia: Ref<IClaimMedia | null> = ref<IClaimMedia | null>(
      props.selectedMedia
        ? (props.selectedMedia as IClaimMedia)
        : props.media?.length
        ? (props.media[0] as IClaimMedia)
        : null
    );

    const onToggleCheckbox = () => {
      if (!currentMedia.value) return;

      currentMedia.value.isPrivate = !currentMedia.value.isPrivate;

      // have to define by using camelCase, due to the browser warning
      // [Vue warn]: Extraneous non-emits event listeners (toggleShare) were passed to component but could not be automatically inherited because component renders fragment or text root nodes. If the listener is intended to be a component custom event listener only, declare it using the "emits" option.
      // eslint-disable-next-line vue/custom-event-name-casing
      emit('toggleShare', currentMedia.value);
    };

    const selectMedia = (media: IClaimMedia) => {
      currentMedia.value = media;
    };

    return {
      currentMedia,
      selectMedia,
      onToggleCheckbox,
    };
  },
});
</script>

<style lang="scss">
.claim-media-gallery-viewer {
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-content: stretch;
  align-items: stretch;

  .claim-media-thumbs {
    height: 100%;
    flex: 0 1 auto;
    display: flex;
    flex-direction: column;
    width: calc(10rem + 4px);
    min-width: calc(10rem + 4px);
    overflow-y: scroll;
    padding-right: 1rem;

    div {
      margin-bottom: 1rem;
    }

    img {
      width: 10rem;
      height: 10rem;
      margin-right: var(--space-1);
      margin-bottom: var(--space-1);
      cursor: pointer;
    }

    img.private {
      box-sizing: border-box;
      border-bottom: 5px solid var(--coral);
    }

    img.selected {
      outline: 5px solid var(--blue);
      outline-offset: -5px;
    }
  }

  .claim-selected-media {
    position: relative;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-content: stretch;
    align-items: center;
    max-width: 100%;
    width: 100%;

    .selected-media-main {
      flex: 1 1 auto;
      align-self: center;
      height: calc(100% - 72px);
      max-height: calc(100% - 72px);
      max-width: 100%;
      width: 100%;
      position: relative;

      video {
        width: 100%;
        max-height: 100%;
      }
    }

    .checkbox-share {
      flex: 0 1 auto;
      align-self: center;
      margin-top: var(--space-4);
      margin-bottom: var(--space-4);
      text-align: center;
    }
  }
}
</style>
